import {UIBaseShareImage} from './UIBaseShareImage.ts'
import {createDivIn, createExternalImgIn, createParagraphIn} from '../ui_utils/DomUtils.ts'
import {MapSettingEnum, VictoryPointStateReader} from '@colonist/game-base-types'
import {UIImagePathController} from '../UIEnums.ts'
import {SectionBgView} from '../ui_canvas_engine/SectionBgView.ts'
import {MAX_USERNAME_LENGTH} from '@colonist/consts'
import {PlayerColor} from '@colonist/game-states'
import RibbonShort from '@static/images/ribbon_short.svg'
import IconCrown from '@static/images/icon_crown.svg'
import {type PlayerValidator} from '@colonist/game-mechanic-validators'

export class UIEndGameShareImage extends UIBaseShareImage {
    static activeHumansAtGameEnd: Set<PlayerColor> = new Set()
    private winningColor: PlayerColor
    protected contentContainer: HTMLDivElement

    protected async generateImage() {
        this.contentContainer = createDivIn(this.container, 'share-image-content-container')
        await this.createMap()
        this.createPlayers()
    }

    private async createMap() {
        const mapView = this.uiGameManager.mapController.mapView

        const viewportBackgroundIndex = 0

        const isElementMapBackground = mapView.getChildAt(viewportBackgroundIndex) instanceof SectionBgView
        if(isElementMapBackground) mapView.removeChildAt(viewportBackgroundIndex)

        const mapImage = await this.extract.image(mapView, 'image/png')
        mapImage.classList.add('share-image-map')

        if(this.isWideMap()) mapImage.classList.add('wide-map')
        if(this.isTallMap()) mapImage.classList.add('tall-map')

        this.contentContainer.appendChild(mapImage)
    }

    private createPlayers() {
        this.setWinningPlayerColor()

        const playerColors = this.uiGameManager.gameController.getPlayerColors()
        this.sortPlayerColors(playerColors)

        const playersDiv = createDivIn(this.contentContainer, 'share-image-players')
        const topRow = createDivIn(playersDiv, 'share-image-players-row')
        const bottomRow = createDivIn(playersDiv, 'share-image-players-row')

        for(let i = 0; i < playerColors.length; i++) {
            const shouldBeOnTopRow = UIEndGameShareImage.isTopRowPlayer(i, playerColors.length)
            const row = shouldBeOnTopRow ? topRow : bottomRow
            const playerDiv = createDivIn(row, 'share-image-player')

            const playerColor = playerColors[i]
            const player = this.uiGameManager.gameController.getPlayerState(playerColor)

            this.createPlayerAvatar(playerDiv, player)
            this.createPlayerRibbon(playerDiv, player)
            UIEndGameShareImage.createPlayerUsername(playerDiv, player.username)

            if(!this.shouldCreatePlayerCrown(playerColor, playerColors.length)) continue

            UIEndGameShareImage.createPlayerCrown(playerDiv)
        }

        UIEndGameShareImage.setPlayersStyle(playersDiv, playerColors.length)
    }

    // eslint-disable-next-line class-methods-use-this
    protected createPlayerAvatar(container: HTMLDivElement, player: PlayerValidator) {
        const iconContainer = createDivIn(container, 'share-image-player-background')
        iconContainer.style.backgroundImage = `url('${UIImagePathController.playerBackgroundByColor[player.color]}')`

        if(player.profilePictureUrl != undefined) {
            UIEndGameShareImage.createPlayerProfilePicture(iconContainer, player.profilePictureUrl)
            return
        }

        UIEndGameShareImage.createPlayerIcon(iconContainer, player)
    }

    private static createPlayerProfilePicture(container: HTMLDivElement, profilePictureUrl: string) {
        const profilePicture = createDivIn(container, `share-image-player-profile-picture`)
        profilePicture.style.backgroundImage = `url('${profilePictureUrl}')`
    }

    private static createPlayerIcon(container: HTMLDivElement, player: PlayerValidator) {
        const iconClass = 'share-image-player-icon'
        const icon = UIEndGameShareImage.activeHumansAtGameEnd.has(player.color) ? player.userState.databaseIcon : player.displayIcon
        const iconSrc = UIImagePathController.getImgPath(icon)

        createExternalImgIn(container, iconSrc, {key: 'strings:colonistCommon.utils.emptyString'}, iconClass)
    }

    private createPlayerRibbon(container: HTMLDivElement, player: PlayerValidator) {
        createExternalImgIn(container, RibbonShort, {key: 'strings:colonistCommon.utils.emptyString'}, 'share-image-player-ribbon')

        const pointsContainer = createDivIn(container, 'share-image-points-container')

        const endGamePlayer = this.endGameState.players[player.color]
        const victoryPoints = endGamePlayer ? VictoryPointStateReader.totalPrivateVictoryPoints(endGamePlayer.victoryPoints) : 0
        createParagraphIn(pointsContainer, {key: 'strings:colonistCommon.utils.#buffer', options: {value: victoryPoints}}, 'share-image-victory-points')
    }

    private static createPlayerUsername(container: HTMLDivElement, username: string) {
        const truncatedUsername = UIEndGameShareImage.getTruncatedUsername(username)
        createParagraphIn(container, {key: 'strings:colonistCommon.utils.#buffer', options: {value: truncatedUsername}}, 'share-image-player-username')
    }

    private static createPlayerCrown(container: HTMLDivElement) {
        createExternalImgIn(container, IconCrown, {key: 'strings:colonistCommon.utils.emptyString'}, 'share-image-player-crown')
    }

    private isWideMap(): boolean {
        const wideMaps: MapSettingEnum[] = [MapSettingEnum.Earth, MapSettingEnum.EarthSeafarers, MapSettingEnum.USA]
        return wideMaps.includes(this.uiGameManager.gameController.gameSettings.mapSetting)
    }

    private isTallMap(): boolean {
        const tallMaps: MapSettingEnum[] = [MapSettingEnum.UK, MapSettingEnum.UKSeafarers]
        return tallMaps.includes(this.uiGameManager.gameController.gameSettings.mapSetting)
    }

    private setWinningPlayerColor() {
        const players = Object.values(this.endGameState.players)
        const winningPlayer = players.find((player) => player.rank == 1)
        this.winningColor = winningPlayer?.color ?? PlayerColor.None
    }

    private static isTopRowPlayer(playerIndex: number, numPlayers: number): boolean {
        if(numPlayers < 3) return true
        if(playerIndex > ((numPlayers - 1) / 2)) return true
        return false
    }

    protected shouldCreatePlayerCrown(playerColor: PlayerColor, numPlayers: number): boolean {
        if(numPlayers == 1) return false
        return playerColor == this.winningColor
    }

    protected sortPlayerColors(playerColors: PlayerColor[]) {
        const winnerIndex = playerColors.findIndex((playerColor) => playerColor == this.winningColor)

        if(winnerIndex == -1) return

        const lastPlayer = playerColors[playerColors.length - 1]
        playerColors[playerColors.length - 1] = playerColors[winnerIndex]
        playerColors[winnerIndex] = lastPlayer
    }

    private static getTruncatedUsername(username: string): string {
        if(username.length <= MAX_USERNAME_LENGTH) return username

        const charSpaceForDots = 2
        const truncatedUsername = username.substring(0, MAX_USERNAME_LENGTH - charSpaceForDots)
        return truncatedUsername + '...'
    }

    private static setPlayersStyle(element: HTMLElement, numPlayers: number) {
        switch(numPlayers) {
            case 3: element.classList.add('three-players'); return
            case 4: element.classList.add('four-players'); return
            case 5: element.classList.add('five-players'); return
            case 6: element.classList.add('six-players'); return
            case 7: element.classList.add('seven-players'); return
            case 8: element.classList.add('eight-players'); return
            default: return
        }
    }
}
